// Spec 078 — volet glissant (Pattern A) de suivi d'une exécution d'audit (FR-017).
// US1 progression, US3 affichage de l'échec (failureCode + message ; code brut si inconnu — MVP
// défensif), US4 détail via la timeline. Charte Pragmelis.

import { useState, useEffect } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/platform/components/ui/sheet'
import { Button } from '@/platform/components/ui/button'
import { useAuditProgress } from '../../hooks/useAuditProgress'
import { AuditProgressTimeline } from './AuditProgressTimeline'
import { failureCodeLabel, failureCodeHint, warningLabel, warningHint } from '../../lib/auditReference'
import type { AuditProgressState } from '../../types/audit'
import { useRetourDuFocus } from '@/platform/hooks/useRetourDuFocus'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string | null
  displayName?: string
  /** Avis à afficher en tête du volet (« Audit lancé pour … »), quand le volet s'ouvre sur un lancement. */
  avis?: string | null
}

const STATE_BANNER: Record<AuditProgressState, { text: string; color: string }> = {
  none: { text: 'Aucun audit récent pour ce client.', color: 'hsl(var(--foreground))' },
  starting: { text: 'Démarrage de l\'audit en cours — les premières étapes apparaîtront sous peu.', color: 'hsl(var(--info-text))' },
  running: { text: 'Audit en cours.', color: 'hsl(var(--info-text))' },
  completed: { text: 'Audit terminé.', color: 'hsl(var(--ok))' },
  failed: { text: 'L\'audit a échoué.', color: 'hsl(var(--destructive))' },
  unavailable: { text: 'Suivi momentanément indisponible — nouvelle tentative automatique.', color: 'hsl(var(--warning))' },
}

function fmtTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

// Indicateur d'actualisation auto : décompte (1 tick/s) jusqu'à la prochaine actualisation.
function AutoRefreshIndicator({ nextRefreshAt, loading }: { nextRefreshAt: number; loading: boolean }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const secs = Math.max(0, Math.ceil((nextRefreshAt - now) / 1000))
  return (
    <span className="text-caption text-muted-foreground">
      {loading ? 'Actualisation…' : `Actualisation auto · prochaine dans ${secs} s`}
    </span>
  )
}

export function AuditProgressSheet({ open, onOpenChange, accountId, displayName, avis = null }: Props) {
  const { progress, loading, error, refresh, nextRefreshAt } = useAuditProgress(open ? accountId : null)
  const retour = useRetourDuFocus()
  const banner = progress ? STATE_BANNER[progress.state] : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[480px] max-w-[90vw] overflow-y-auto sm:max-w-[480px]"
        // Ouvert sans déclencheur propre (menu de la date du bandeau, bouton « Suivre ») : rendre le focus à l'origine.
        onOpenAutoFocus={() => retour.memoriser()}
        onCloseAutoFocus={retour.rendre}
      >
        <SheetHeader>
          <SheetTitle className="font-heading text-primary">
            Suivi de l'audit{displayName ? ` — ${displayName}` : ''}
          </SheetTitle>
          <SheetDescription className="text-small text-muted-foreground">
            {progress?.executionName ? `Exécution ${progress.executionName}` : 'Progression de la dernière exécution'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Spec 161, second retour (2026-10-12) — lancé depuis le bandeau, l'audit dit ici qu'il est parti. */}
          {avis && <p role="status" className="text-body text-primary">{avis}</p>}
          {loading && !progress && <p className="text-body text-muted-foreground">Chargement du suivi…</p>}

          {banner && (
            <div
              className="rounded-[3px] border px-3 py-2 text-body"
              style={{ borderColor: 'hsl(var(--border))', color: banner.color }}
            >
              {banner.text}
            </div>
          )}

          {/* US3 — échec : motif structuré (milestone v1.4 : failureCode + cause) ou générique (repli ARM) */}
          {progress?.failure && (
            <div className="rounded-[3px] border px-3 py-2" style={{ borderColor: 'hsl(var(--destructive))', background: 'hsl(var(--critical-light))' }}>
              <p className="text-small font-semibold" style={{ color: 'hsl(var(--destructive))' }}>
                Échec — {failureCodeLabel(progress.failure.failureCode)}
              </p>
              <p className="mt-1 text-body text-muted-foreground">{progress.failure.message}</p>
              {failureCodeHint(progress.failure.failureCode) && (
                <p className="mt-1 text-small" style={{ color: 'hsl(var(--primary))' }}>
                  → {failureCodeHint(progress.failure.failureCode)}
                </p>
              )}
              <p className="mt-1 text-caption text-muted-foreground">
                Code : {progress.failure.failureCode} · source :{' '}
                {progress.failure.source === 'milestone' ? 'jalon d\'exécution' : 'statut d\'exécution'}
              </p>
            </div>
          )}

          {/* Spec 082 (US3) — réserves de qualité de données remontées par l'exécution (ex.
              REPORTS_ANONYMIZED). Bloc non bloquant, affiché même en état completed.
              Dédup par code : le runbook émet le même warning sur plusieurs milestones (jalon + step)
              → on n'affiche chaque réserve qu'une fois (emplacement unique ; la timeline ne les répète pas). */}
          {progress?.warnings && progress.warnings.length > 0 && (
            <div className="space-y-2">
              {[...new Map(progress.warnings.map(w => [w.code, w])).values()].map((w, i) => {
                const color = w.severity === 'info' ? 'hsl(var(--info-text))' : 'hsl(var(--warning-text))'
                const bg = w.severity === 'info' ? 'hsl(var(--info-light))' : 'hsl(var(--warning-light))'
                const hint = w.remediation ?? warningHint(w.code)
                return (
                  <div key={`${w.code}-${i}`} className="rounded-[3px] border px-3 py-2" style={{ borderColor: color, background: bg }}>
                    <p className="text-small font-semibold" style={{ color }}>
                      {warningLabel(w.code)}
                    </p>
                    {w.message && <p className="mt-1 text-body text-muted-foreground">{w.message}</p>}
                    {hint && (
                      <p className="mt-1 text-small" style={{ color: 'hsl(var(--primary))' }}>→ {hint}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {progress && progress.state !== 'none' && progress.state !== 'unavailable' && (
            <AuditProgressTimeline
              jalons={progress.jalons}
              currentSeq={progress.currentSeq}
              total={progress.total}
            />
          )}

          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="flex flex-col">
              {nextRefreshAt !== null
                ? <AutoRefreshIndicator nextRefreshAt={nextRefreshAt} loading={loading} />
                : <span className="text-caption text-muted-foreground">Actualisation automatique arrêtée (audit terminé).</span>}
              <span className="text-caption text-muted-foreground">
                {progress?.lastUpdated ? `Dernière mise à jour : ${fmtTime(progress.lastUpdated)}` : ''}
                {error ? ` · ${error}` : ''}
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
              {loading ? 'Actualisation…' : 'Actualiser maintenant'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
